import { useMemo } from "react";
import { CreditCard, Tag, Calendar } from "lucide-react";
import { useTransactions } from "@/hooks/useTransactions";
import { useBankTransactions } from "@/hooks/useBankTransactions";
import { useDashboardRange } from "./DashboardContext";
import { useAnonymize, anonymizeCategory, anonymizeAmount } from "@/components/AnonymizeContext";

function isDateInCurrentPeriod(date: string, range: "month" | "year") {
  if (!date) return false;
  const now = new Date();
  const d = new Date(date);
  if (range === "month") {
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  } else {
    return d.getFullYear() === now.getFullYear();
  }
}

const CARD_COLORS: Record<string, string> = {
  "Chase Freedom": "bg-violet-100 text-violet-700",
  "Amex Blue": "bg-blue-100 text-blue-700",
  "Capital One": "bg-pink-100 text-pink-700",
  "Bank ACH": "bg-green-100 text-green-700",
  "Amex": "bg-blue-100 text-blue-700",
};

const monthName = new Date().toLocaleString("default", { month: "long" });
const year = new Date().getFullYear();

export default function SummaryStats() {
  const range = useDashboardRange();
  const { data: transactions = [], isLoading: isLoadingTxns, error: errorTxns } = useTransactions();
  const { data: bankTxns = [], isLoading: isLoadingBank, error: errorBank } = useBankTransactions();
  const { anonymize } = useAnonymize();

  const cleanedBankTxns = useMemo(() => bankTxns.filter((t) => t.type), [bankTxns]);
  const cleanedAppTxns = useMemo(() => transactions.filter((t) => t.type), [transactions]);

  const allTxns = useMemo(() => [
    ...cleanedAppTxns.map(t => ({ ...t, source: "app" })),
    ...cleanedBankTxns.map(t => ({ ...t, source: "bank" })),
  ], [cleanedAppTxns, cleanedBankTxns]);

  const filteredAllTxns = useMemo(() => (
    allTxns.filter(t => isDateInCurrentPeriod(t.date, range))
  ), [allTxns, range]);

  const incomeTxns = useMemo(() => (
    filteredAllTxns.filter(t => t.type === "Income" && t.amount > 0)
  ), [filteredAllTxns]);
  
  const investmentTxns = useMemo(() => (
    filteredAllTxns.filter(
      t => t.category === "Investment" && t.amount < 0
    )
  ), [filteredAllTxns]);
  
  const spendingTxns = useMemo(() => (
    filteredAllTxns.filter(
      t => t.type === "Expense" && t.amount < 0 && 
      t.category !== "Investment" && t.category !== "Credit Card Payment"
    )
  ), [filteredAllTxns]);

  const totalIncome = useMemo(() => (
    incomeTxns.reduce((sum, t) => sum + t.amount, 0)
  ), [incomeTxns]);
  const totalInvestment = useMemo(() => (
    investmentTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0)
  ), [investmentTxns]);
  const totalSpending = useMemo(() => (
    spendingTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0)
  ), [spendingTxns]);

  const byCategoryForTop = useMemo(() => {
    const categories: Record<string, number> = {};
    spendingTxns.forEach(t => {
      categories[t.category] = (categories[t.category] || 0) + Math.abs(t.amount);
    });
    return categories;
  }, [spendingTxns]);

  const topCategory = useMemo(() => {
    const entries = Object.entries(byCategoryForTop);
    if (entries.length === 0) return null;
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    if (anonymize) return [anonymizeCategory(sorted[0][0], 0), sorted[0][1]];
    return sorted[0];
  }, [byCategoryForTop, anonymize]);

  const numTransactions = filteredAllTxns.length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div className="bg-white rounded-xl shadow p-5 flex flex-col items-start">
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-700">
          <Calendar size={22} className="text-primary" />
          Total Spent ({range === "month" ? monthName : year})
        </span>
        <span className="text-2xl font-bold text-violet-600 mt-2">
          {anonymize
            ? anonymizeAmount(totalSpending)
            : `$${totalSpending.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </span>
      </div>
      <div className="bg-white rounded-xl shadow p-5 flex flex-col items-start">
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-700">
          <Tag size={22} className="text-primary" />
          Total Income
        </span>
        <span className="text-2xl font-bold text-green-600 mt-2">
          {anonymize
            ? anonymizeAmount(totalIncome)
            : `$${totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </span>
      </div>
      <div className="bg-white rounded-xl shadow p-5 flex flex-col items-start">
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-700">
          <Tag size={22} className="text-primary" />
          Total Investment
        </span>
        <span className="text-2xl font-bold text-blue-600 mt-2">
          {anonymize
            ? anonymizeAmount(totalInvestment)
            : `$${totalInvestment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        </span>
      </div>
      <div className="bg-white rounded-xl shadow p-5 flex flex-col items-start">
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-700">
          <Tag size={22} className="text-primary" />
          Top Category
        </span>
        {topCategory ? (
          <>
            <span className="text-lg">{topCategory[0]}</span>
            <span className="font-bold text-indigo-500">
              {anonymize
                ? anonymizeAmount(Number(topCategory[1]))
                : `$${topCategory[1].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </span>
          </>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </div>
      <div className="bg-white rounded-xl shadow p-5 flex flex-col items-start">
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-700">
          <CreditCard size={22} className="text-primary" />
          # of Transactions
        </span>
        <span className="text-xl font-bold text-fuchsia-500 mt-2">{numTransactions}</span>
      </div>
    </div>
  );
}
